import { MatrizRiesgoService } from "../matriz-riesgo.service";
import { MatrizEvidencia } from "src/app/shared/model/iso42001/matriz-riesgo.model";

/*
 * Evidencias adjuntas de la matriz (activos y riesgos de proceso): lo que las
 * hojas de Activos y del Plan Gestión RSK hacen igual para abrir, bajar y
 * describir un archivo, y para explicar por qué no se pudo subir. Son
 * funciones sueltas para que también las pueda usar quien no hereda de
 * HojaMatrizBase, como los expedientes.
 */

/**
 * Sólo PDF, imágenes y video se abren en una pestaña, y con el tipo que manda
 * el servidor (sale de la extensión). El mime guardado no: en renglones viejos
 * lo declaraba el navegador al subir y podía ser text/html, que en un blob
 * corre con el origen de la app. Lo demás se baja.
 */
const SE_ABRE_EN_PESTANA = /^(application\/pdf|image\/(jpeg|png|gif|webp|bmp)|video\/mp4)$/;

/** Lo que vive la liga temporal del archivo antes de soltarla. */
const VIDA_LIGA_MS = 30000;

/** Baja el archivo con su nombre, por una liga temporal. */
function bajarComo(url: string, evd: MatrizEvidencia): void {
  const liga = document.createElement("a");
  liga.href = url;
  liga.download = evd.nombreArchivo || "evidencia";
  liga.click();
}

/** Abre la evidencia en una pestaña si ahí se puede ver; si no, la baja. */
export function abrirEvidencia(service: MatrizRiesgoService, evd: MatrizEvidencia): void {
  if (!evd.idEvidencia) {
    return;
  }
  service.descargarEvidencia(evd.idEvidencia).subscribe((datos) => {
    const tipo = (datos.type || "").toLowerCase();
    const url = URL.createObjectURL(datos);
    if (SE_ABRE_EN_PESTANA.test(tipo)) {
      window.open(url, "_blank");
    } else {
      bajarComo(url, evd);
    }
    setTimeout(() => URL.revokeObjectURL(url), VIDA_LIGA_MS);
  });
}

/** Baja la evidencia, sea del tipo que sea. */
export function bajarEvidencia(service: MatrizRiesgoService, evd: MatrizEvidencia): void {
  if (!evd.idEvidencia) {
    return;
  }
  service.descargarEvidencia(evd.idEvidencia).subscribe((blob) => {
    const url = URL.createObjectURL(blob);
    bajarComo(url, evd);
    setTimeout(() => URL.revokeObjectURL(url), VIDA_LIGA_MS);
  });
}

/** El ícono de la evidencia, por su tipo o por su extensión. */
export function iconoDeEvidencia(evd: MatrizEvidencia): string {
  if (evd.tipoArchivo === "IMAGEN") {
    return "🖼";
  }
  if (evd.tipoArchivo === "VIDEO") {
    return "🎬";
  }
  const n = (evd.nombreArchivo || "").toLowerCase();
  if (n.endsWith(".pdf")) {
    return "📄";
  }
  if (n.endsWith(".xls") || n.endsWith(".xlsx") || n.endsWith(".csv")) {
    return "📊";
  }
  return "📎";
}

/** "Corte del 13/07/2026", si la evidencia respalda un corte concreto; si no, vacío. */
export function corteDeEvidencia(evd: MatrizEvidencia): string {
  if (!evd.fechaSeguimiento) {
    return "";
  }
  const f = String(evd.fechaSeguimiento);
  const soloDia = /^(\d{4})-(\d{2})-(\d{2})/.exec(f);
  if (soloDia) {
    return "Corte del " + soloDia[3] + "/" + soloDia[2] + "/" + soloDia[1];
  }
  const d = new Date(evd.fechaSeguimiento);
  return Number.isNaN(d.getTime()) ? "" : "Corte del " + d.toLocaleDateString("es-MX");
}

/** "820 B", "3.2 KB", "1.5 MB"; vacío si no se sabe. */
export function tamanoDeArchivo(bytes?: number): string {
  if (bytes === null || bytes === undefined) {
    return "";
  }
  if (bytes < 1024) {
    return bytes + " B";
  }
  if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(1) + " KB";
  }
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

/** Lo que llega en el error de la subida: la respuesta HTTP fallida. */
export interface ErrorSubida {
  status?: number;
  error?: { mensajeError?: string };
}

/**
 * Por qué no se pudo subir. El código HTTP dice mucho: 404 = el backend no
 * trae todavía el endpoint (falta recompilar), 401 = la sesión caducó, 413 =
 * archivo demasiado grande. Decirlo ahorra adivinar.
 */
export function detalleErrorSubida(e: ErrorSubida | null | undefined): string {
  const estado = e?.status;
  if (estado === 404) {
    return "El servidor no tiene todavía el módulo de evidencias. Falta desplegar el backend.";
  }
  if (estado === 401) {
    return "La sesión caducó. Vuelve a entrar e inténtalo de nuevo.";
  }
  if (estado === 413) {
    return "El archivo es demasiado grande.";
  }
  // Ojo con el 0: es falsy, y tratarlo como "sin código" no distinguía
  // "reventó el servidor" de "no hubo respuesta".
  const codigo = estado === undefined ? "?" : String(estado);
  const sinRespuesta = estado === 0 ? ": el navegador no recibió respuesta del servidor" : "";
  const mensaje = e?.error?.mensajeError ? " " + e.error.mensajeError : "";
  return "Error al subir la evidencia (HTTP " + codigo + sinRespuesta + ")." + mensaje;
}
